import React from 'react';
import { Box } from '@chakra-ui/react';
import { NavDropdownItem, DROPDOWN_GAP } from '../navigationData';
import DropdownCard from './DropdownCard';

interface DropdownPanelProps {
  items: NavDropdownItem[];
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onItemClick: () => void;
  position: { top: number; left: number };
  gradientId: string;
  variant?: 'default' | 'learn-more';
}

const SITE_PADDING = 24; // Match website's horizontal padding

const DropdownPanel: React.FC<DropdownPanelProps> = ({ items, onMouseEnter, onMouseLeave, onItemClick, position, gradientId, variant = 'default' }) => {
  const isLearnMore = variant === 'learn-more';
  const knowledgeHub = isLearnMore ? items[0] : null;
  const articles = isLearnMore ? items.slice(1) : items;
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [adjustedLeft, setAdjustedLeft] = React.useState(position.left);

  // Adjust position to keep dropdown within viewport bounds
  React.useEffect(() => {
    if (panelRef.current) {
      const panelWidth = panelRef.current.offsetWidth;
      const viewportWidth = window.innerWidth;
      const centeredLeft = position.left - panelWidth / 2;

      // Clamp position within padding bounds
      const minLeft = SITE_PADDING;
      const maxLeft = viewportWidth - panelWidth - SITE_PADDING;

      const clampedLeft = Math.max(minLeft, Math.min(centeredLeft, maxLeft));
      setAdjustedLeft(clampedLeft + panelWidth / 2);
    }
  }, [position.left]);

  // Column width: 280px ideal, but shrink to fit if needed
  // minmax(200px, 280px) allows columns to shrink on narrow screens
  const columnTemplate = isLearnMore
    ? 'repeat(3, minmax(180px, 280px))'
    : items.length <= 4
      ? 'repeat(2, minmax(200px, 280px))'
      : 'repeat(3, minmax(180px, 280px))';

  return (
    <Box
      ref={panelRef}
      position="fixed"
      top={`${position.top}px`}
      left={`${adjustedLeft}px`}
      transform="translateX(-50%)"
      zIndex={101}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      pt={`${DROPDOWN_GAP}px`}
      maxW={`calc(100vw - ${SITE_PADDING * 2}px)`}
      role="menu"
      aria-label="Navigation submenu"
    >
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <linearGradient id="desktop-gradient-purple-pink" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#EC4899" />
          </linearGradient>
          <linearGradient id="desktop-gradient-indigo-teal" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#14B8A6" />
          </linearGradient>
        </defs>
      </svg>

      <Box
        className="dropdown-panel"
        bg="white"
        borderRadius="2xl"
        boxShadow="2xl"
        p={5}
      >
        {isLearnMore && knowledgeHub ? (
          <>
            {/* Knowledge Hub - full width */}
            <Box mb={4}>
              <DropdownCard item={knowledgeHub} gradientId={gradientId} onClick={onItemClick} />
            </Box>

            {/* Separator */}
            <Box
              borderBottom="1px solid"
              borderColor="gray.200"
              mb={4}
            />

            {/* Articles subtitle */}
            <Box
              fontFamily="lato"
              fontWeight="bold"
              fontSize="xs"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              mb={3}
              px={1}
            >
              Articles
            </Box>

            {/* Articles grid */}
            <Box
              display="grid"
              gridTemplateColumns={columnTemplate}
              gridAutoRows="1fr"
              gap={4}
              role="group"
            >
              {articles.map((item) => (
                <DropdownCard key={item.href} item={item} gradientId={gradientId} onClick={onItemClick} />
              ))}
            </Box>
          </>
        ) : (
          <Box
            display="grid"
            gridTemplateColumns={columnTemplate}
            gridAutoRows="1fr"
            gap={4}
            role="group"
          >
            {items.map((item) => (
              <DropdownCard key={item.href} item={item} gradientId={gradientId} onClick={onItemClick} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DropdownPanel;
